import React from 'react';
import { Play, Image as ImageIcon, Box } from 'lucide-react';

export default function MediaCard({ item, kind, onOpen, index = 0 }) {
  const type = item.type === 'model' || (!item.type && kind === '3d') ? 'model' : item.type || kind;
  const isVideo = type === 'video';
  const is3D = type === 'model';
  const poster = item.poster || (isVideo ? null : item.src);
  const Icon = is3D ? Box : isVideo ? Play : ImageIcon;

  return (
    <button
      onClick={() => onOpen(item)}
      className="tv-card text-left group"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-[#0a0502]">
        {is3D ? (
          <div className="absolute inset-0 flex items-center justify-center"
               style={{ background: `radial-gradient(circle at 50% 40%, ${item.color}44 0%, #0a0502 70%)` }}>
            <div className="relative">
              <div className="w-24 h-24 rounded-full border-2 border-dashed animate-spin"
                   style={{ borderColor: item.color, animationDuration: '12s' }} />
              <div className="absolute inset-3 rounded-full"
                   style={{ background: `${item.color}aa`, boxShadow: `0 0 40px ${item.color}aa` }} />
              <div className="absolute inset-0 flex items-center justify-center">
                <Box className="w-8 h-8 text-[#1a0e05]" />
              </div>
            </div>
          </div>
        ) : !poster && isVideo ? (
          // No cover uploaded: show the video's own first frame.
          <video
            src={`${item.src}#t=0.1`}
            preload="metadata"
            muted
            playsInline
            aria-label={item.title}
            data-testid={`media-card-video-thumb-${item.id}`}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500 pointer-events-none"
          />
        ) : (
          <img
            src={poster}
            alt={item.title}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
            loading="lazy"
          />
        )}

        <div className="absolute inset-0 pointer-events-none" style={{
          background: 'repeating-linear-gradient(to bottom, rgba(0,0,0,0) 0 2px, rgba(0,0,0,0.28) 3px 4px)',
          mixBlendMode: 'multiply'
        }} />

        <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-[#1a0e05]/85 border border-[#f5a623]/50 px-2 py-1 rounded">
          <div className="w-1.5 h-1.5 rounded-full bg-[#ff4a2a] animate-pulse" />
          <span className="font-osd text-xs text-[#f5a623] tracking-wider">REC</span>
        </div>

        {(isVideo || is3D) && (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="w-14 h-14 rounded-full bg-[#f5a623] text-[#1a0e05] flex items-center justify-center border-2 border-[#1a0e05] shadow-[3px_3px_0_#1a0e05]">
              <Icon className="w-6 h-6 ml-0.5" />
            </div>
          </div>
        )}

        {item.runtime && (
          <div className="absolute bottom-2 right-2 bg-[#1a0e05]/90 font-osd text-xs text-[#ffe9b8] px-2 py-0.5 rounded border border-[#f5a623]/40">
            {item.runtime}
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-xl text-[#ffe9b8] leading-tight">{item.title}</h3>
          <span className="font-osd text-xs text-[#f5a623] whitespace-nowrap mt-1">{formatDate(item.date)}</span>
        </div>
        <p className="font-body text-[13px] leading-[1.5] text-[#a88552] mt-2 line-clamp-2 min-h-[39px]">{item.description}</p>
        {(item.medium || item.software) && (
          <div className="mt-3 pt-3 border-t border-[#2a1608] font-osd text-xs text-[#8b6a3e]">
            {item.medium || item.software}
          </div>
        )}
      </div>
    </button>
  );
}

function formatDate(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' }).toUpperCase();
  } catch { return d; }
}
